import { useCall } from "@stream-io/video-react-sdk";
import { useCallback, useEffect, useState } from "react";
import join from "../assets/sounds/joined.wav";
import left from "../assets/sounds/left.wav";

export const useNotificationSoundAndToast = () => {
  const [toastData, setToastData] = useState<{
    message: string;
    image?: string;
  } | null>(null);
  const call = useCall();

  // Check if the event is triggered by the current user
  const isSelf = useCallback(
    (userId: string) => userId === call?.currentUserId,
    [call]
  );

  useEffect(() => {
    if (!call) return;

    const unlistenJoin = call.on("call.session_participant_joined", (event) => {
      if (!isSelf(event.participant.user.id)) {
        playSoundFromUrl(join);
        setToastData({
          message: `${event.participant.user.name} has joined the call.`,
          image: event.participant.user.image,
        });

        setTimeout(() => setToastData(null), 1000);
      }
    });

    const unlistenLeft = call.on("call.session_participant_left", (event) => {
      if (!isSelf(event.participant.user.id)) {
        playSoundFromUrl(left);
        setToastData({
          message: `${event.participant.user.name} has left the call.`,
          image: event.participant.user.image,
        });

        setTimeout(() => setToastData(null), 1000);
      }
    });

    return () => {
      unlistenJoin();
      unlistenLeft();
    };
  }, [call, isSelf]);

  return { toastData, setToastData };
};

const audioCache = new Map<string, () => Promise<void>>();

export async function playSoundFromUrl(url: string) {
  let doPlay = audioCache.get(url);

  if (!doPlay) {
    // Wait for an audio file to load
    const canPlayPromise = new Promise<HTMLAudioElement>((resolve) => {
      const audio = new Audio(url);
      audio.addEventListener("canplaythrough", () => resolve(audio), {
        once: true,
      });
    });

    doPlay = async () => {
      const audio = await canPlayPromise;
      await audio.play();
    };

    // Save to cache
    audioCache.set(url, doPlay);
  }

  await doPlay();
}
